/**
 * Customer Journey List Hook
 * Used for QualifiedProspects and CoolLead tabs
 */
import { useMemo, useEffect, useCallback, createElement, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import {
    getCustomersQualifiedProspects,
    getCustomerCoolLead,
} from '@/modules/Customer/services'
import type { CustomerQualifiedProspect } from '@/modules/Customer/types'
import type { ResponseArray } from '@/types/shared'
import { FormField } from '@/components/ui/form-field'
import { paths } from '@/utils/path'
import usePermissionsStore from '@/stores/usePermissionsStore'
import dayjs from 'dayjs'
import {
    getJourneyCustomerColumns,
    getJourneyExpandedColumns,
    generateWeekColumns,
    generateJourneyWeekColumns,
    getJourneyFilterItems,
    JOURNEY_LIST_FILTER_DEFAULTS,
    JOURNEY_SORT_CONFIG,
    getCustomerJourneys,
    hasJourneys,
    getDefaultNoteTimeRange,
} from '../utils/customer-journey-list-config'

interface JourneyFilters {
    customer_sale_followers: number[]
    customer_industry: string[]
    customer_service_interestings: string[]
    customer_created_at: string[]
    customer_source: string[]
    customer_source_type: string[]
    note_gte?: string
    note_lte?: string
    only_with_notes?: boolean
}

type ListType = 'qualified-prospects' | 'cool-lead'

// Cột tiền ẩn với Marketing (chỉ xem KH convert từ lead)
const LEAD_ORIGIN_HIDDEN_JOURNEY_COLUMN_KEYS = ['budget']

/** Get default note_gte/note_lte as ISO strings */
const getDefaultNoteDates = () => {
    const [from, to] = getDefaultNoteTimeRange()
    return {
        note_gte: from.toISOString(),
        note_lte: to.toISOString(),
    }
}

export const useCustomerJourneyList = (
    type: ListType = 'qualified-prospects'
) => {
    const { t } = useTranslation('customer')
    const navigate = useNavigate()
    const { canViewLeadOriginCustomers } = usePermissionsStore()

    const defaultDates = useMemo(() => getDefaultNoteDates(), [])

    const filterDefaults = useMemo(
        () => ({
            ...JOURNEY_LIST_FILTER_DEFAULTS,
            ...defaultDates,
            only_with_notes: false,
        }),
        [defaultDates]
    )

    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        setFilters,
        searchInputProps,
    } = useUrlSearchParams<JourneyFilters>({
        defaults: {
            page: 1,
            page_size: 20,
            ...JOURNEY_SORT_CONFIG.defaultSort,
        },
        filterDefaults,
    })

    const { control, watch } = useForm<JourneyFilters, any, JourneyFilters>({
        defaultValues: filters,
        values: filters,
    })

    // Sync form changes to URL filters
    const watchedNoteGte = watch('note_gte')
    const watchedNoteLte = watch('note_lte')
    const watchedOnlyWithNotes = watch('only_with_notes')

    const prevFilterValues = useRef({
        noteGte: watchedNoteGte,
        noteLte: watchedNoteLte,
        onlyWithNotes: watchedOnlyWithNotes,
    })

    useEffect(() => {
        const noteGteStr = watchedNoteGte
            ? dayjs(watchedNoteGte).toISOString()
            : undefined
        const noteLteStr = watchedNoteLte
            ? dayjs(watchedNoteLte).toISOString()
            : undefined

        // Only update URL filters when values actually change (setFilters resets page to 1)
        const prev = prevFilterValues.current
        if (
            prev.noteGte === watchedNoteGte &&
            prev.noteLte === watchedNoteLte &&
            prev.onlyWithNotes === watchedOnlyWithNotes
        ) {
            return
        }
        prevFilterValues.current = {
            noteGte: watchedNoteGte,
            noteLte: watchedNoteLte,
            onlyWithNotes: watchedOnlyWithNotes,
        }

        setFilters({
            note_gte: noteGteStr,
            note_lte: noteLteStr,
            only_with_notes: watchedOnlyWithNotes,
        } as Partial<JourneyFilters>)
    }, [watchedNoteGte, watchedNoteLte, watchedOnlyWithNotes, setFilters])

    // Derive noteTimeRange from filters for week columns
    const noteGte = useMemo(
        () => dayjs(filters.note_gte || defaultDates.note_gte),
        [filters.note_gte, defaultDates]
    )
    const noteLte = useMemo(
        () => dayjs(filters.note_lte || defaultDates.note_lte),
        [filters.note_lte, defaultDates]
    )

    const noteTimeRange = useMemo(
        () => [noteGte, noteLte] as [dayjs.Dayjs, dayjs.Dayjs],
        [noteGte, noteLte]
    )

    const apiCall =
        type === 'qualified-prospects'
            ? getCustomersQualifiedProspects
            : getCustomerCoolLead

    const queryPayload = {
        page: params.page,
        page_size: params.page_size,
        q: queryParams.q,
        sort_by: params.sort_by,
        sort_order: params.sort_order,
        filters: queryParams.filters,
    }

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS, type, queryParams],
        queryFn: () =>
            apiCall(queryPayload) as Promise<
                ResponseArray<CustomerQualifiedProspect>
            >,
        placeholderData: true,
    })

    const customers = data?.results || []
    const pagination = data?.pagination

    // Memoized columns
    const columns = useMemo(() => getJourneyCustomerColumns(t), [t])
    const journeyColumns = useMemo(() => {
        const cols = getJourneyExpandedColumns(t)
        if (!canViewLeadOriginCustomers) return cols
        return cols.filter(
            (col) =>
                !LEAD_ORIGIN_HIDDEN_JOURNEY_COLUMN_KEYS.includes(
                    String(col.key)
                )
        )
    }, [t, canViewLeadOriginCustomers])
    const filterItems = useMemo(
        () => [
            ...getJourneyFilterItems(t),
            {
                name: 'note_date_range',
                type: 'custom' as const,
                component: createElement(FormField, {
                    control,
                    name: ['note_gte', 'note_lte'],
                    type: 'daterangepicker',
                    label: t('note_time_range'),
                    numberOfMonths: 2,
                    showPresets: true,
                } as any),
            },
            {
                name: 'only_with_notes',
                type: 'custom' as const,
                component: createElement(FormField, {
                    control,
                    name: 'only_with_notes',
                    type: 'checkbox',
                    label: t('only_with_notes'),
                    bordered: true,
                } as any),
            },
        ],
        [t, control]
    )

    // Dynamic week columns based on note time range
    const weekColumns = useMemo(
        () => generateWeekColumns(noteTimeRange, t),
        [noteTimeRange, t]
    )

    // Dynamic week columns for expanded journey rows
    const journeyWeekColumns = useMemo(
        () => generateJourneyWeekColumns(noteTimeRange, t),
        [noteTimeRange, t]
    )

    // Combined columns for main table (base + week columns)
    const allColumns = useMemo(
        () => [...columns, ...weekColumns] as typeof columns,
        [columns, weekColumns]
    )

    // Combined columns for expanded rows (base journey + week columns)
    const allJourneyColumns = useMemo(
        () =>
            [...journeyColumns, ...journeyWeekColumns] as typeof journeyColumns,
        [journeyColumns, journeyWeekColumns]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (size: number) => setParams({ page_size: size }),
        [setParams]
    )

    // Sort handler
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handleAdd =
        type === 'qualified-prospects'
            ? () => navigate(paths.customerCreate)
            : undefined

    return {
        // Data
        customers,
        pagination,
        stateComponent: renderState(),

        // Params & filters
        params,
        control,
        setFilter,
        searchInputProps,

        // Columns
        columns: allColumns,
        journeyColumns: allJourneyColumns,
        filterItems,
        weekColumns,

        // Handlers
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        handleAdd,

        // Utilities
        getCustomerJourneys,
        hasJourneys,
        noteTimeRange,

        // Translation
        t,
    }
}
